import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";
import { toast } from "sonner";
import { trackAddToCart } from "../lib/metaPixel";

const CartContext = createContext({
  cart: { items: [], subtotal: 0, count: 0 },
  isInCart: () => false,
  addToCart: async () => {},
  removeFromCart: async () => {},
  updateQuantity: async () => {},
  clearCart: async () => {},
  isCartOpen: false,
  setIsCartOpen: () => {},
  openCart: () => {},
  closeCart: () => {},
  refresh: () => {}
});

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [cart, setCart] = useState({ items: [], subtotal: 0, count: 0 });
  const [isCartOpen, setIsCartOpen] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) {
      setCart({ items: [], subtotal: 0, count: 0 });
      return;
    }
    try {
      const { data } = await api.get("/cart");
      setCart(data || { items: [], subtotal: 0, count: 0 });
    } catch (e) {
      console.error("Failed to load cart", e);
      setCart({ items: [], subtotal: 0, count: 0 });
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const isInCart = (productIdOrObj) => {
    const pid = typeof productIdOrObj === "object" && productIdOrObj !== null
      ? (productIdOrObj.id || productIdOrObj.product_id)
      : productIdOrObj;
    return cart.items.some((item) => item.product_id === pid);
  };

  const addToCart = async (productIdOrObj, quantity = 1, options = {}) => {
    if (!user) {
      toast.error("Please login to add items to your cart");
      return false;
    }
    const productId = typeof productIdOrObj === "object" && productIdOrObj !== null
      ? (productIdOrObj.id || productIdOrObj.product_id)
      : productIdOrObj;
    if (!productId) {
      toast.error("Invalid product selected");
      return false;
    }
    try {
      const { data } = await api.post("/cart", { product_id: String(productId), quantity: Math.max(1, parseInt(quantity, 10) || 1) });
      setCart(data);
      const isB2B = options?.isWholesale || false;
      const countMsg = data?.count ? ` (${data.count} ${data.count === 1 ? "item" : "items"})` : "";
      toast.success(isB2B ? `Added to Wholesale Cart!${countMsg}` : `Added to Cart!${countMsg}`, {
        action: {
          label: "View Cart",
          onClick: () => setIsCartOpen(true)
        }
      });
      if (options?.product) {
        trackAddToCart(options.product, quantity);
      } else {
        const itemObj = data?.items?.find(i => i.product_id === productId);
        if (itemObj) {
          trackAddToCart(itemObj, quantity);
        }
      }
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add to cart");
      return false;
    }
  };

  const removeFromCart = async (productId) => {
    if (!user) return;
    try {
      const { data } = await api.delete(`/cart/${productId}`);
      setCart(data);
      toast.success("Item removed from cart");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove item");
    }
  };

  const updateQuantity = async (productId, quantity) => {
    if (!user) return;
    try {
      const { data } = await api.put(`/cart/${productId}`, { quantity });
      setCart(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update quantity");
    }
  };

  const clearCart = async () => {
    if (!user) return;
    try {
      await api.delete("/cart");
      setCart({ items: [], subtotal: 0, count: 0 });
    } catch (e) {
      console.error(e);
    }
  };

  const openCart = () => setIsCartOpen(true);
  const closeCart = () => setIsCartOpen(false);

  return (
    <CartContext.Provider
      value={{
        cart,
        isInCart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        isCartOpen,
        setIsCartOpen,
        openCart,
        closeCart,
        refresh
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
